import { DocumentNode, gql, TypedDocumentNode } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { CategoryPreferences } from '@segment/consent-manager/types/types';
import { print } from 'graphql';
import { isbot } from 'isbot';
import debounce from 'lodash-es/debounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { CookieStatistics } from '@pflegia/types/api/cookie-statistics/cookie-statistics';
import { useCookiesConsentManagerContext } from '@pflegia/ui/contexts/cookies-consent-manager.context';
import { getSourceDomainFromClient } from '@pflegia/util/pflegia/source-domain/get-source-domain-from-client';
import { transformBackendEndpoint } from '@pflegia/util/pflegia/source-domain/transform-backend-endpoint';

const SHOW_DEBOUNCE_TIME = 1000;

export const cookieStatisticsState = { hasShownGlobally: false, hasDecidedGlobally: false };

export const shouldSendStatistics = (): boolean => !navigator.webdriver && !isbot(navigator.userAgent);

export const DEFAULT_UPDATE_COOKIE_CONSENT_STATISTICS_MUTATION: TypedDocumentNode<
  { updateCookieConsentStatistics: boolean },
  { key: string }
> = gql`
  mutation UpdateCookieConsentStatistics($key: String!) {
    updateCookieConsentStatistics(key: $key)
  }
`;

const sendLeaveStatistic = (mutation: DocumentNode) => {
  const endpoint = transformBackendEndpoint(process.env.NEXT_PUBLIC_BACKEND_ENDPOINT ?? '', getSourceDomainFromClient());
  if (!endpoint) return;

  fetch(endpoint, {
    method: 'POST',
    keepalive: true,
    credentials: 'omit',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ query: print(mutation), variables: { key: CookieStatistics.Leave } }),
  }).catch(() => undefined);
};

const useCookieStatistics = (cookieConsentStatisticsUpdateMutation: DocumentNode = DEFAULT_UPDATE_COOKIE_CONSENT_STATISTICS_MUTATION) => {
  const { updateCookiesSettings } = useCookiesConsentManagerContext();
  const [updateStatistics] = useMutation(cookieConsentStatisticsUpdateMutation, { onError: () => undefined });

  useEffect(() => {
    if (!shouldSendStatistics()) return;
    const handlePageHide = () => {
      if (cookieStatisticsState.hasDecidedGlobally || !cookieStatisticsState.hasShownGlobally) return;
      cookieStatisticsState.hasDecidedGlobally = true;
      sendLeaveStatistic(cookieConsentStatisticsUpdateMutation);
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [cookieConsentStatisticsUpdateMutation]);

  const setCookies = useCallback(
    async (cookies: boolean | CategoryPreferences) => {
      cookieStatisticsState.hasDecidedGlobally = true;
      if (shouldSendStatistics())
        await updateStatistics({
          variables: {
            key: getCookieStatisticValue(cookies),
          },
        });
      updateCookiesSettings(cookies === true ? { advertising: true, marketingAndAnalytics: true } : cookies || {});
    },
    [updateCookiesSettings, updateStatistics],
  );

  const dismissCookies = useCallback(async () => {
    cookieStatisticsState.hasDecidedGlobally = true;
    const grantedPreferences = { advertising: true, marketingAndAnalytics: true };
    if (shouldSendStatistics()) await updateStatistics({ variables: { key: CookieStatistics.Dismiss } });
    updateCookiesSettings(grantedPreferences);
  }, [updateCookiesSettings, updateStatistics]);

  const showSettings = useCallback(async () => {
    if (shouldSendStatistics()) await updateStatistics({ variables: { key: CookieStatistics.Settings } });
  }, [updateStatistics]);

  const showBannerArgsRef = useRef({ updateStatistics });
  showBannerArgsRef.current = { updateStatistics };
  const showBanner = useMemo(() => {
    const trySendShow = async () => {
      if (cookieStatisticsState.hasShownGlobally) return;
      if (document.visibilityState !== 'visible') {
        document.addEventListener(
          'visibilitychange',
          () => {
            trySendShow().catch(() => undefined);
          },
          { once: true },
        );
        return;
      }
      cookieStatisticsState.hasShownGlobally = true;
      const args = showBannerArgsRef.current;
      if (shouldSendStatistics()) await args.updateStatistics({ variables: { key: CookieStatistics.Show } });
    };

    return debounce(trySendShow, SHOW_DEBOUNCE_TIME);
  }, []);

  return {
    setCookies,
    dismissCookies,
    showSettings,
    showBanner,
  };
};

const getCookieStatisticValue = (cookies: boolean | CategoryPreferences) => {
  if (typeof cookies === 'object') {
    const { advertising, marketingAndAnalytics } = cookies;
    if (advertising && marketingAndAnalytics) return CookieStatistics.All;
    if (advertising) return CookieStatistics.AdvertisingOnly;
    if (marketingAndAnalytics) return CookieStatistics.AnalyticsOnly;
    return CookieStatistics.None;
  }

  return cookies ? CookieStatistics.All : CookieStatistics.None;
};

export default useCookieStatistics;
